import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { clienteName } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { TextField, SelectField, Field } from "../components/Field";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const EMPTY = { tipo: "privato", nome: "", cognome: "", ragione_sociale: "", codice_fiscale: "", partita_iva: "", email: "", telefono: "", indirizzo: "", citta: "", cap: "", note: "" };

export const ClienteDialog = ({ open, onClose, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]]));
      const r = initial?.id ? await api.put(`/clienti/${initial.id}`, payload) : await api.post("/clienti", payload);
      toast.success("Cliente salvato");
      onSaved(r.data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="cliente-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{initial?.id ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField label="Tipo" value={f.tipo} onChange={set("tipo")} testId="cliente-tipo-select"
            options={[{ value: "privato", label: "Privato" }, { value: "azienda", label: "Azienda" }]} />
          {f.tipo === "azienda" ? <TextField label="Ragione sociale" value={f.ragione_sociale} onChange={set("ragione_sociale")} testId="cliente-ragione-sociale-input" /> : <div />}
          <TextField label={f.tipo === "azienda" ? "Nome referente" : "Nome *"} value={f.nome} onChange={set("nome")} testId="cliente-nome-input" />
          <TextField label="Cognome" value={f.cognome} onChange={set("cognome")} testId="cliente-cognome-input" />
          <TextField label="Codice fiscale" value={f.codice_fiscale} onChange={set("codice_fiscale")} testId="cliente-cf-input" className="[&_input]:font-mono [&_input]:uppercase" />
          <TextField label="Partita IVA" value={f.partita_iva} onChange={set("partita_iva")} testId="cliente-piva-input" />
          <TextField label="Email" type="email" value={f.email} onChange={set("email")} testId="cliente-email-input" />
          <TextField label="Telefono" value={f.telefono} onChange={set("telefono")} testId="cliente-telefono-input" />
          <TextField label="Indirizzo" value={f.indirizzo} onChange={set("indirizzo")} testId="cliente-indirizzo-input" className="sm:col-span-2" />
          <TextField label="Città" value={f.citta} onChange={set("citta")} testId="cliente-citta-input" />
          <TextField label="CAP" value={f.cap} onChange={set("cap")} testId="cliente-cap-input" />
          <Field label="Note" className="sm:col-span-2"><Textarea value={f.note} onChange={(e) => set("note")(e.target.value)} data-testid="cliente-note-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cliente-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={!f.nome} className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="cliente-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Clienti() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/clienti", { params: { q } }).then((r) => setItems(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const remove = async (c) => {
    if (!window.confirm(`Eliminare ${clienteName(c)}?`)) return;
    try { await api.delete(`/clienti/${c.id}`); toast.success("Cliente eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Clienti" subtitle="Anagrafica clienti della concessionaria">
        <Button onClick={() => { setEdit(null); setOpen(true); }} className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="new-cliente-btn"><Plus className="mr-1 h-4 w-4" /> Nuovo cliente</Button>
      </PageHeader>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Cerca nome, CF, P.IVA, email…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white pl-9" data-testid="clienti-search-input" />
      </div>
      <div className="overflow-x-auto rounded-md border bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Cliente</TableHead><TableHead>CF / P.IVA</TableHead><TableHead>Contatti</TableHead><TableHead>Città</TableHead><TableHead className="w-24" />
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Nessun cliente</TableCell></TableRow>}
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`cliente-row-${c.id}`}>
                <TableCell><div className="font-semibold">{clienteName(c)}</div><div className="text-xs capitalize text-muted-foreground">{c.tipo}</div></TableCell>
                <TableCell className="font-mono text-xs">{c.codice_fiscale || c.partita_iva || "—"}</TableCell>
                <TableCell className="text-xs"><div>{c.telefono}</div><div className="text-muted-foreground">{c.email}</div></TableCell>
                <TableCell className="text-sm">{c.citta || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => { setEdit(c); setOpen(true); }} data-testid={`edit-cliente-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(c)} data-testid={`delete-cliente-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ClienteDialog open={open} onClose={() => setOpen(false)} initial={edit} onSaved={load} />
    </div>
  );
}
